import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom'
import { TrainingPage } from '../features/training/TrainingPage'
import { ImageInboxPage } from '../features/training/ImageInboxPage'
import { ChatAdjustmentsPage } from '../features/training/ChatAdjustmentsPage'
import { SendAlertPage } from '../features/notifications/SendAlertPage'
import { useAuth } from '../features/auth/AuthContext'
import { LoginPage } from '../features/auth/LoginPage'
import { TvApprovalPage } from '../features/auth/TvApprovalPage'
import { PasswordResetPage } from '../features/auth/PasswordResetPage'
import { AppShell } from './AppShell'
import { DashboardPage } from '../features/dashboard/DashboardPage'
import { ClientHomePage } from '../features/dashboard/ClientHomePage'
import { TransmissionPage } from '../features/monitoring/TransmissionPage'
import { PersonalMonitorPage } from '../features/monitoring/PersonalMonitorPage'
import { EventsPage } from '../features/events/EventsPage'
import { RecordingsPage } from '../features/recordings/RecordingsPage'
import { CompaniesDirectoryPage as CompaniesPage } from '../features/companies/CompaniesDirectoryPage'
import { SettingsPage } from '../features/settings/SettingsPage'
import { ErrorsHubPage } from '../features/errors/ErrorsHubPage'
import { PerformancePage } from '../features/performance/PerformancePage'
import { FullAdministrationPage as AdministrationPage } from '../features/administration/FullAdministrationPage'
import { ModelsPage } from '../features/models/ModelsPage'
import { LaboratoryManagementPage as LaboratoryPage } from '../features/laboratory/LaboratoryManagementPage'
import { LaboratoryPage as LiveTestPage } from '../features/laboratory/LaboratoryPage'
import { LandingPage } from '../features/public/LandingPage'
import { LegalPage } from '../features/public/LegalPage'
import { InvitationEntry } from '../features/auth/InvitationEntry'
import { ClientSettingsPage } from '../features/workspace/ClientSettingsPage'
import { RaspberryPage } from '../features/administration/RaspberryPage'
import { LocalMonitorPage } from '../features/administration/LocalMonitorPage'
import { ClientPlanPage } from '../features/workspace/ClientPlanPage'
import { OffersPage } from '../features/workspace/OffersPage'
import { StorageRequestsPanel } from '../features/workspace/StorageCapacityPanel'
import { SpectraBusinessPage } from '../features/workspace/SpectraBusinessPage'
import { ModuleChoicePage } from '../features/auth/ModuleChoicePage'
import { PortariaPage } from '../features/portaria/PortariaPage'
import { ResidentPortariaPage } from '../features/portaria/ResidentPortariaPage'

function ProtectedApp() {
  const { user, access } = useAuth()
  if (!user) return <Navigate to="/login" replace />
  if (!access.hasVigilanciaAccess) return <Navigate to="/modulos" replace />
  return <AppShell />
}

function ProtectedPortaria() {
  const { user, access } = useAuth()
  if (!user) return <Navigate to="/login" replace />
  if (!access.hasPortariaAccess) return <Navigate to="/modulos" replace />
  return <PortariaPage />
}

function TransmissionEntry() {
  const [params] = useSearchParams()
  return params.get('personalizado') === '1' ? <PersonalMonitorPage /> : <TransmissionPage />
}

function PersonalMonitorRedirect() {
  const [params] = useSearchParams()
  params.set('personalizado', '1')
  return <Navigate replace to={`/central/transmissao?${params}`} />
}

export function App() {
  const { loading, user, access } = useAuth()
  if (loading) return <div className="boot"><span className="spinner" /><strong>Preparando o Spectra Guard AI</strong></div>
  return (
    <Routes>
      <Route path="/tv" element={<TvApprovalPage />} />
      <Route path="/" element={<InvitationEntry fallback={user ? <Navigate to="/modulos" replace /> : <LandingPage />} />} />
      <Route path="/modulos" element={user ? <ModuleChoicePage /> : <Navigate to="/login" replace />} />
      <Route path="/login" element={user ? <Navigate to="/modulos" replace /> : <LoginPage />} />
      <Route path="/portaria" element={<ProtectedPortaria />} />
      <Route path="/portaria/entrada" element={<Navigate to={user ? '/portaria' : '/login'} replace />} />
      <Route path="/portaria/morador" element={<ResidentPortariaPage />} />
      <Route path="/cadastro" element={<Navigate to={user ? '/modulos' : '/login'} replace />} />
      <Route path="/recuperar-senha" element={user ? <Navigate to="/central" replace /> : <PasswordResetPage />} />
      <Route path="/termos" element={<LegalPage document="terms" />} />
      <Route path="/privacidade" element={<LegalPage document="privacy" />} />
      <Route path="/central" element={<ProtectedApp />}>
        <Route index element={access.clientOnly && !access.hasScopedEventAccess ? <ClientHomePage /> : <DashboardPage />} />
        <Route path="transmissao" element={<TransmissionEntry />} />
        <Route path="transmissao-pessoal" element={<PersonalMonitorRedirect />} />
        <Route path="transmissao-personalizada" element={<TransmissionPage forcedProcessed />} />
        <Route path="gravacoes" element={<RecordingsPage />} />
        <Route path="eventos" element={access.clientOnly?<Navigate to="../transmissao" replace/>:<EventsPage />} />
        <Route path="eventos-empresa" element={access.hasScopedEventAccess?<EventsPage globalScope />:!access.clientOnly&&access.hasCompanyAccess?<EventsPage companyWideScope />:<Navigate to=".." replace/>} />
        <Route path="eventos-gerais" element={access.isGeneralAdministrator?<EventsPage globalScope />:<Navigate to=".." replace/>} />
        <Route path="relatorio" element={<Navigate to={access.isGeneralAdministrator?'../eventos-gerais':'../eventos-empresa'} replace/>} />
        <Route path="empresas" element={<CompaniesPage />} />
        <Route path="raspberry" element={access.isGeneralAdministrator?<RaspberryPage />:<Navigate to=".." replace/>} />
        <Route path="local" element={access.isGeneralAdministrator?<LocalMonitorPage />:<Navigate to=".." replace/>} />
        <Route path="modelos" element={access.isGeneralAdministrator?<ModelsPage />:<Navigate to=".." replace/>} />
        <Route path="treinamento" element={access.isGeneralAdministrator?<TrainingPage />:<Navigate to=".." replace/>} />
        <Route path="minhas-imagens" element={access.isGeneralAdministrator?<ImageInboxPage />:<Navigate to=".." replace/>} />
        <Route path="ajustes-do-chat" element={access.isGeneralAdministrator?<ChatAdjustmentsPage />:<Navigate to=".." replace/>} />
        <Route path="teste-ao-vivo" element={access.isGeneralAdministrator?<LaboratoryPage/>:!access.hasCompanyAccess?<LiveTestPage/>:<Navigate to=".." replace/>} />
        <Route path="administracao" element={access.isGeneralAdministrator?<AdministrationPage />:<Navigate to=".." replace/>} />
        <Route path="performance" element={access.isGeneralAdministrator?<PerformancePage />:<Navigate to=".." replace/>} />
        <Route path="erros" element={access.isGeneralAdministrator||access.accountAdministrator?<ErrorsHubPage />:<Navigate to=".." replace/>} />
        <Route path="diagnosticos" element={<Navigate to=".." replace />} />
        <Route path="erros-cameras" element={access.isGeneralAdministrator?<Navigate to="/central/erros?tab=cameras" replace/>:<Navigate to=".." replace/>} />
        <Route path="cameras-offline" element={access.isGeneralAdministrator||access.accountAdministrator?<Navigate to="/central/erros?tab=offline" replace/>:<Navigate to=".." replace/>} />
        <Route path="cliente-configuracoes" element={<ClientSettingsPage />} />
        <Route path="plano" element={<Navigate to="../plano-empresa" replace/>} />
        <Route path="plano-empresa" element={access.isGeneralAdministrator||access.accountAdministrator?<ClientPlanPage />:<Navigate to="../" replace/>} />
        <Route path="ofertas" element={access.isGeneralAdministrator||access.accountAdministrator?<OffersPage />:<Navigate to="../" replace/>} />
        <Route path="solicitacoes" element={access.isGeneralAdministrator?<StorageRequestsPanel />:<Navigate to="../" replace/>} />
        <Route path="spectra" element={access.isGeneralAdministrator?<SpectraBusinessPage />:<Navigate to="../" replace/>} />
        <Route path="enviar-alerta" element={access.isGeneralAdministrator||access.accountAdministrator?<SendAlertPage />:<Navigate to=".." replace/>} />
        <Route path="conta" element={<SettingsPage />} />
        <Route path="configuracoes" element={<Navigate to="../conta" replace />} />
      </Route>
      <Route path="*" element={<Navigate to={user ? '/modulos' : '/'} replace />} />
    </Routes>
  )
}
